import React, { useState, useEffect } from 'react';
import type { W8BENFormData } from '../types';

interface W8BENQuestionnaireProps {
  bearerToken: string;
  language?: string;
  prefillData?: Partial<W8BENFormData>;
  onSubmit: () => void;
  onSuccess: (result: any) => void;
  onError: (error: Error) => void;
  onProgress: (step: number, total: number) => void;
  environment?: 'production' | 'sandbox';
  debug?: boolean;
}

export const W8BENQuestionnaire: React.FC<W8BENQuestionnaireProps> = ({
  bearerToken,
  prefillData,
  onSubmit,
  onSuccess,
  onError,
  onProgress,
  debug = false,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form state
  const [formData, setFormData] = useState<Partial<W8BENFormData>>({
    name: '',
    countryOfCitizenship: '',
    permanentResidenceAddress: '',
    permanentResidenceCity: '',
    permanentResidenceCountry: '',
    permanentResidencePostalCode: '',
    mailingAddress: '',
    mailingCity: '',
    mailingCountry: '',
    mailingPostalCode: '',
    dateOfBirth: '',
    usTin: '',
    usTinType: undefined,
    foreignTin: '',
    foreignTinCountry: '',
    referenceNumbers: [],
    claimTreatyBenefits: false,
    treatyCountry: '',
    treatyArticle: '',
    withholdingRate: undefined,
    incomeType: '',
    treatyExplanation: '',
    beneficialOwnerType: 'individual',
    signatureName: '',
    signingCapacity: '',
    esignConsent: false,
    ...prefillData,
  });

  const totalSteps = 5;

  useEffect(() => {
    onProgress(currentStep, totalSteps);
  }, [currentStep, onProgress, totalSteps]);

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.name?.trim()) {
        newErrors.name = 'Name is required';
      }
      if (!formData.countryOfCitizenship?.trim()) {
        newErrors.countryOfCitizenship = 'Country of citizenship is required';
      }
      if (!formData.permanentResidenceAddress?.trim()) {
        newErrors.permanentResidenceAddress = 'Permanent residence address is required';
      }
      if (!formData.permanentResidenceCity?.trim()) {
        newErrors.permanentResidenceCity = 'City is required';
      }
      if (!formData.permanentResidenceCountry?.trim()) {
        newErrors.permanentResidenceCountry = 'Country is required';
      }
      if (!formData.dateOfBirth?.trim()) {
        newErrors.dateOfBirth = 'Date of birth is required';
      } else {
        const date = new Date(formData.dateOfBirth);
        if (isNaN(date.getTime())) {
          newErrors.dateOfBirth = 'Invalid date format';
        }
      }
    } else if (step === 2) {
      // Tax ID validation (both are optional but validate format if provided)
      if (formData.usTin && formData.usTin.trim()) {
        const tinDigits = formData.usTin.replace(/[^0-9]/g, '');
        if (tinDigits.length !== 9) {
          newErrors.usTin = 'U.S. TIN must be 9 digits';
        }
        if (!formData.usTinType) {
          newErrors.usTinType = 'Please specify U.S. TIN type';
        }
      }
      if (formData.foreignTin && formData.foreignTin.trim() && !formData.foreignTinCountry) {
        newErrors.foreignTinCountry = 'Please specify foreign TIN country';
      }
    } else if (step === 3) {
      if (formData.claimTreatyBenefits) {
        if (!formData.treatyCountry?.trim()) {
          newErrors.treatyCountry = 'Treaty country is required when claiming benefits';
        }
        if (!formData.treatyArticle?.trim()) {
          newErrors.treatyArticle = 'Treaty article is required';
        }
        if (formData.withholdingRate === undefined || formData.withholdingRate === null || (typeof formData.withholdingRate === 'string' && formData.withholdingRate === '')) {
          newErrors.withholdingRate = 'Withholding rate is required';
        } else {
          const rate = typeof formData.withholdingRate === 'string'
            ? parseFloat(formData.withholdingRate)
            : formData.withholdingRate;
          if (isNaN(rate) || rate < 0 || rate > 100) {
            newErrors.withholdingRate = 'Rate must be between 0 and 100';
          }
        }
        if (!formData.incomeType?.trim()) {
          newErrors.incomeType = 'Income type is required';
        }
      }
    } else if (step === 4) {
      if (!formData.beneficialOwnerType) {
        newErrors.beneficialOwnerType = 'Beneficial owner type is required';
      }
    } else if (step === 5) {
      if (!formData.signatureName?.trim()) {
        newErrors.signatureName = 'Signature name is required';
      }
      if (!formData.esignConsent) {
        newErrors.esignConsent = 'You must consent to electronic signature';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleChange = (field: keyof W8BENFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) {
      return;
    }

    setIsSubmitting(true);
    onSubmit();

    try {
      const response = await fetch('/api/submissions/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${bearerToken}`,
        },
        body: JSON.stringify({
          form_type: 'w8ben',
          form_data: {
            ...formData,
            userAgent: navigator.userAgent,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Submission failed');
      }

      const result = await response.json();
      onSuccess(result);
    } catch (error) {
      console.error('W-8 BEN submission error:', error);
      onError(error instanceof Error ? error : new Error('Unknown error occurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="w8ben-step">
            <h3 className="w8ben-step-title">Part I: Identification of Beneficial Owner</h3>
            <p className="w8ben-step-description">
              Provide your identifying information as a foreign individual.
            </p>

            <div className="w8ben-form-group">
              <label className="w8ben-label" htmlFor="name">
                Name (Individual) <span className="w8ben-required">*</span>
              </label>
              <input
                id="name"
                type="text"
                className={`w8ben-input ${errors.name ? 'w8ben-input-error' : ''}`}
                value={formData.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter your full legal name"
              />
              {errors.name && <span className="w8ben-error-message">{errors.name}</span>}
              <p className="w8ben-field-hint">Your name as it appears on your passport or ID</p>
            </div>

            <div className="w8ben-form-group">
              <label className="w8ben-label" htmlFor="countryOfCitizenship">
                Country of Citizenship <span className="w8ben-required">*</span>
              </label>
              <input
                id="countryOfCitizenship"
                type="text"
                className={`w8ben-input ${errors.countryOfCitizenship ? 'w8ben-input-error' : ''}`}
                value={formData.countryOfCitizenship || ''}
                onChange={(e) => handleChange('countryOfCitizenship', e.target.value)}
                placeholder="e.g., Canada, United Kingdom, Japan"
              />
              {errors.countryOfCitizenship && (
                <span className="w8ben-error-message">{errors.countryOfCitizenship}</span>
              )}
            </div>

            <div className="w8ben-form-section">
              <h4 className="w8ben-section-title">Permanent Residence Address</h4>
              <p className="w8ben-section-hint">
                Do not use a P.O. box or in-care-of address. This should be your actual residence.
              </p>

              <div className="w8ben-form-group">
                <label className="w8ben-label" htmlFor="permanentResidenceAddress">
                  Street Address <span className="w8ben-required">*</span>
                </label>
                <input
                  id="permanentResidenceAddress"
                  type="text"
                  className={`w8ben-input ${errors.permanentResidenceAddress ? 'w8ben-input-error' : ''}`}
                  value={formData.permanentResidenceAddress || ''}
                  onChange={(e) => handleChange('permanentResidenceAddress', e.target.value)}
                  placeholder="Street address"
                />
                {errors.permanentResidenceAddress && (
                  <span className="w8ben-error-message">{errors.permanentResidenceAddress}</span>
                )}
              </div>

              <div className="w8ben-form-row">
                <div className="w8ben-form-group w8ben-form-group-half">
                  <label className="w8ben-label" htmlFor="permanentResidenceCity">
                    City <span className="w8ben-required">*</span>
                  </label>
                  <input
                    id="permanentResidenceCity"
                    type="text"
                    className={`w8ben-input ${errors.permanentResidenceCity ? 'w8ben-input-error' : ''}`}
                    value={formData.permanentResidenceCity || ''}
                    onChange={(e) => handleChange('permanentResidenceCity', e.target.value)}
                    placeholder="City"
                  />
                  {errors.permanentResidenceCity && (
                    <span className="w8ben-error-message">{errors.permanentResidenceCity}</span>
                  )}
                </div>

                <div className="w8ben-form-group w8ben-form-group-half">
                  <label className="w8ben-label" htmlFor="permanentResidenceCountry">
                    Country <span className="w8ben-required">*</span>
                  </label>
                  <input
                    id="permanentResidenceCountry"
                    type="text"
                    className={`w8ben-input ${errors.permanentResidenceCountry ? 'w8ben-input-error' : ''}`}
                    value={formData.permanentResidenceCountry || ''}
                    onChange={(e) => handleChange('permanentResidenceCountry', e.target.value)}
                    placeholder="Country"
                  />
                  {errors.permanentResidenceCountry && (
                    <span className="w8ben-error-message">{errors.permanentResidenceCountry}</span>
                  )}
                </div>
              </div>

              <div className="w8ben-form-group">
                <label className="w8ben-label" htmlFor="permanentResidencePostalCode">
                  Postal Code
                </label>
                <input
                  id="permanentResidencePostalCode"
                  type="text"
                  className="w8ben-input"
                  value={formData.permanentResidencePostalCode || ''}
                  onChange={(e) => handleChange('permanentResidencePostalCode', e.target.value)}
                  placeholder="Postal/ZIP code (optional)"
                />
              </div>
            </div>

            <div className="w8ben-form-section">
              <h4 className="w8ben-section-title">Mailing Address (if different)</h4>

              <div className="w8ben-form-group">
                <label className="w8ben-label" htmlFor="mailingAddress">
                  Street Address
                </label>
                <input
                  id="mailingAddress"
                  type="text"
                  className="w8ben-input"
                  value={formData.mailingAddress || ''}
                  onChange={(e) => handleChange('mailingAddress', e.target.value)}
                  placeholder="Mailing address (optional)"
                />
              </div>

              <div className="w8ben-form-row">
                <div className="w8ben-form-group w8ben-form-group-half">
                  <label className="w8ben-label" htmlFor="mailingCity">
                    City
                  </label>
                  <input
                    id="mailingCity"
                    type="text"
                    className="w8ben-input"
                    value={formData.mailingCity || ''}
                    onChange={(e) => handleChange('mailingCity', e.target.value)}
                    placeholder="City (optional)"
                  />
                </div>

                <div className="w8ben-form-group w8ben-form-group-half">
                  <label className="w8ben-label" htmlFor="mailingCountry">
                    Country
                  </label>
                  <input
                    id="mailingCountry"
                    type="text"
                    className="w8ben-input"
                    value={formData.mailingCountry || ''}
                    onChange={(e) => handleChange('mailingCountry', e.target.value)}
                    placeholder="Country (optional)"
                  />
                </div>
              </div>
            </div>

            <div className="w8ben-form-group">
              <label className="w8ben-label" htmlFor="dateOfBirth">
                Date of Birth <span className="w8ben-required">*</span>
              </label>
              <input
                id="dateOfBirth"
                type="date"
                className={`w8ben-input ${errors.dateOfBirth ? 'w8ben-input-error' : ''}`}
                value={formData.dateOfBirth || ''}
                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              />
              {errors.dateOfBirth && (
                <span className="w8ben-error-message">{errors.dateOfBirth}</span>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="w8ben-step">
            <h3 className="w8ben-step-title">Part II: Tax Identification</h3>
            <p className="w8ben-step-description">
              Provide your taxpayer identification numbers (optional but recommended).
            </p>

            <div className="w8ben-info-box">
              <h4>About Tax Identification Numbers</h4>
              <p>
                While not required, providing your U.S. or foreign tax identification number helps
                ensure proper tax reporting and may qualify you for reduced withholding rates under
                applicable tax treaties.
              </p>
            </div>

            <div className="w8ben-form-section">
              <h4 className="w8ben-section-title">U.S. Taxpayer Identification Number</h4>

              <div className="w8ben-form-group">
                <label className="w8ben-label" htmlFor="usTin">
                  U.S. TIN (SSN or ITIN)
                </label>
                <input
                  id="usTin"
                  type="text"
                  className={`w8ben-input ${errors.usTin ? 'w8ben-input-error' : ''}`}
                  value={formData.usTin || ''}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9]/g, '');
                    if (value.length <= 9) {
                      if (value.length > 5) {
                        value = value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5);
                      } else if (value.length > 3) {
                        value = value.slice(0, 3) + '-' + value.slice(3);
                      }
                      handleChange('usTin', value);
                    }
                  }}
                  placeholder="XXX-XX-XXXX (optional)"
                  maxLength={11}
                />
                {errors.usTin && <span className="w8ben-error-message">{errors.usTin}</span>}
              </div>

              {formData.usTin && formData.usTin.trim() && (
                <div className="w8ben-form-group">
                  <label className="w8ben-label" htmlFor="usTinType">
                    U.S. TIN Type <span className="w8ben-required">*</span>
                  </label>
                  <select
                    id="usTinType"
                    className={`w8ben-input ${errors.usTinType ? 'w8ben-input-error' : ''}`}
                    value={formData.usTinType || ''}
                    onChange={(e) => handleChange('usTinType', e.target.value)}
                  >
                    <option value="">Select type</option>
                    <option value="SSN">SSN - Social Security Number</option>
                    <option value="ITIN">ITIN - Individual Taxpayer Identification Number</option>
                  </select>
                  {errors.usTinType && (
                    <span className="w8ben-error-message">{errors.usTinType}</span>
                  )}
                </div>
              )}
            </div>

            <div className="w8ben-form-section">
              <h4 className="w8ben-section-title">Foreign Tax Identifying Number</h4>

              <div className="w8ben-form-group">
                <label className="w8ben-label" htmlFor="foreignTin">
                  Foreign TIN
                </label>
                <input
                  id="foreignTin"
                  type="text"
                  className="w8ben-input"
                  value={formData.foreignTin || ''}
                  onChange={(e) => handleChange('foreignTin', e.target.value)}
                  placeholder="Your country's tax ID (optional)"
                />
              </div>

              {formData.foreignTin && formData.foreignTin.trim() && (
                <div className="w8ben-form-group">
                  <label className="w8ben-label" htmlFor="foreignTinCountry">
                    Issuing Country <span className="w8ben-required">*</span>
                  </label>
                  <input
                    id="foreignTinCountry"
                    type="text"
                    className={`w8ben-input ${errors.foreignTinCountry ? 'w8ben-input-error' : ''}`}
                    value={formData.foreignTinCountry || ''}
                    onChange={(e) => handleChange('foreignTinCountry', e.target.value)}
                    placeholder="Country that issued the TIN"
                  />
                  {errors.foreignTinCountry && (
                    <span className="w8ben-error-message">{errors.foreignTinCountry}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="w8ben-step">
            <h3 className="w8ben-step-title">Part III: Claim of Tax Treaty Benefits</h3>
            <p className="w8ben-step-description">
              If your country has a tax treaty with the United States, you may claim reduced
              withholding rates.
            </p>

            <div className="w8ben-form-group">
              <label className="w8ben-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.claimTreatyBenefits || false}
                  onChange={(e) => handleChange('claimTreatyBenefits', e.target.checked)}
                />
                <span>I claim tax treaty benefits</span>
              </label>
            </div>

            {formData.claimTreatyBenefits && (
              <>
                <div className="w8ben-info-box">
                  <h4>Tax Treaty Information</h4>
                  <p>
                    The United States has income tax treaties with several countries. These treaties
                    may provide for reduced rates of withholding on certain types of income.
                  </p>
                </div>

                <div className="w8ben-form-group">
                  <label className="w8ben-label" htmlFor="treatyCountry">
                    Treaty Country <span className="w8ben-required">*</span>
                  </label>
                  <input
                    id="treatyCountry"
                    type="text"
                    className={`w8ben-input ${errors.treatyCountry ? 'w8ben-input-error' : ''}`}
                    value={formData.treatyCountry || ''}
                    onChange={(e) => handleChange('treatyCountry', e.target.value)}
                    placeholder="Country of residence for treaty purposes"
                  />
                  {errors.treatyCountry && (
                    <span className="w8ben-error-message">{errors.treatyCountry}</span>
                  )}
                </div>

                <div className="w8ben-form-row">
                  <div className="w8ben-form-group w8ben-form-group-half">
                    <label className="w8ben-label" htmlFor="treatyArticle">
                      Treaty Article <span className="w8ben-required">*</span>
                    </label>
                    <input
                      id="treatyArticle"
                      type="text"
                      className={`w8ben-input ${errors.treatyArticle ? 'w8ben-input-error' : ''}`}
                      value={formData.treatyArticle || ''}
                      onChange={(e) => handleChange('treatyArticle', e.target.value)}
                      placeholder="e.g., Article 10"
                    />
                    {errors.treatyArticle && (
                      <span className="w8ben-error-message">{errors.treatyArticle}</span>
                    )}
                  </div>

                  <div className="w8ben-form-group w8ben-form-group-half">
                    <label className="w8ben-label" htmlFor="withholdingRate">
                      Withholding Rate (%) <span className="w8ben-required">*</span>
                    </label>
                    <input
                      id="withholdingRate"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      className={`w8ben-input ${errors.withholdingRate ? 'w8ben-input-error' : ''}`}
                      value={formData.withholdingRate || ''}
                      onChange={(e) => handleChange('withholdingRate', e.target.value)}
                      placeholder="e.g., 15"
                    />
                    {errors.withholdingRate && (
                      <span className="w8ben-error-message">{errors.withholdingRate}</span>
                    )}
                  </div>
                </div>

                <div className="w8ben-form-group">
                  <label className="w8ben-label" htmlFor="incomeType">
                    Type of Income <span className="w8ben-required">*</span>
                  </label>
                  <input
                    id="incomeType"
                    type="text"
                    className={`w8ben-input ${errors.incomeType ? 'w8ben-input-error' : ''}`}
                    value={formData.incomeType || ''}
                    onChange={(e) => handleChange('incomeType', e.target.value)}
                    placeholder="e.g., Dividends, Interest, Royalties"
                  />
                  {errors.incomeType && (
                    <span className="w8ben-error-message">{errors.incomeType}</span>
                  )}
                </div>

                <div className="w8ben-form-group">
                  <label className="w8ben-label" htmlFor="treatyExplanation">
                    Additional Explanation (Optional)
                  </label>
                  <textarea
                    id="treatyExplanation"
                    className="w8ben-textarea"
                    value={formData.treatyExplanation || ''}
                    onChange={(e) => handleChange('treatyExplanation', e.target.value)}
                    placeholder="Provide any additional explanation for claiming treaty benefits"
                    rows={4}
                  />
                </div>
              </>
            )}

            {!formData.claimTreatyBenefits && (
              <div className="w8ben-info-box">
                <h4>No Treaty Benefits</h4>
                <p>
                  If you are not claiming tax treaty benefits, the standard U.S. withholding rate
                  of 30% will apply to applicable income.
                </p>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="w8ben-step">
            <h3 className="w8ben-step-title">Part IV: Beneficial Owner Type</h3>
            <p className="w8ben-step-description">
              Specify your status as the beneficial owner of the income.
            </p>

            <div className="w8ben-form-group">
              <label className="w8ben-label">
                Beneficial Owner Type <span className="w8ben-required">*</span>
              </label>
              <div className="w8ben-radio-group">
                <label className="w8ben-radio-label">
                  <input
                    type="radio"
                    name="beneficialOwnerType"
                    value="individual"
                    checked={formData.beneficialOwnerType === 'individual'}
                    onChange={(e) => handleChange('beneficialOwnerType', e.target.value)}
                  />
                  <span>Individual</span>
                </label>

                <label className="w8ben-radio-label">
                  <input
                    type="radio"
                    name="beneficialOwnerType"
                    value="government"
                    checked={formData.beneficialOwnerType === 'government'}
                    onChange={(e) => handleChange('beneficialOwnerType', e.target.value)}
                  />
                  <span>Government</span>
                </label>

                <label className="w8ben-radio-label">
                  <input
                    type="radio"
                    name="beneficialOwnerType"
                    value="international-organization"
                    checked={formData.beneficialOwnerType === 'international-organization'}
                    onChange={(e) => handleChange('beneficialOwnerType', e.target.value)}
                  />
                  <span>International Organization</span>
                </label>

                <label className="w8ben-radio-label">
                  <input
                    type="radio"
                    name="beneficialOwnerType"
                    value="foreign-central-bank"
                    checked={formData.beneficialOwnerType === 'foreign-central-bank'}
                    onChange={(e) => handleChange('beneficialOwnerType', e.target.value)}
                  />
                  <span>Central Bank of Issue</span>
                </label>

                <label className="w8ben-radio-label">
                  <input
                    type="radio"
                    name="beneficialOwnerType"
                    value="exempt-beneficial-owner"
                    checked={formData.beneficialOwnerType === 'exempt-beneficial-owner'}
                    onChange={(e) => handleChange('beneficialOwnerType', e.target.value)}
                  />
                  <span>Tax-Exempt Organization</span>
                </label>

                <label className="w8ben-radio-label">
                  <input
                    type="radio"
                    name="beneficialOwnerType"
                    value="other"
                    checked={formData.beneficialOwnerType === 'other'}
                    onChange={(e) => handleChange('beneficialOwnerType', e.target.value)}
                  />
                  <span>Other</span>
                </label>
              </div>
              {errors.beneficialOwnerType && (
                <span className="w8ben-error-message">{errors.beneficialOwnerType}</span>
              )}
            </div>

            <div className="w8ben-info-box">
              <h4>What is a Beneficial Owner?</h4>
              <p>
                The beneficial owner is the person who owns the income for tax purposes. In most
                cases for individuals receiving payments, this is simply "Individual".
              </p>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="w8ben-step">
            <h3 className="w8ben-step-title">Part V: Certification and Electronic Signature</h3>
            <p className="w8ben-step-description">
              Please review the certifications and provide your electronic signature.
            </p>

            <div className="w8ben-certification-box">
              <h4>Certification</h4>
              <p>Under penalties of perjury, I declare that I have examined the information on this form and to the best of my knowledge and belief it is true, correct, and complete. I further certify under penalties of perjury that:</p>
              <ul>
                <li>I am the individual that is the beneficial owner (or am authorized to sign for the individual that is the beneficial owner) of all the income to which this form relates;</li>
                <li>The person named on line 1 of this form is not a U.S. person;</li>
                <li>The income to which this form relates is not effectively connected with the conduct of a trade or business in the United States;</li>
                <li>For broker transactions or barter exchanges, the beneficial owner is an exempt foreign person as defined in the instructions;</li>
                {formData.claimTreatyBenefits && (
                  <>
                    <li>The beneficial owner is a resident of {formData.treatyCountry || '[treaty country]'} within the meaning of the income tax treaty between the United States and that country;</li>
                    <li>The beneficial owner derives the item (or items) of income for which the treaty benefits are claimed, and meets the treaty limitation on benefits provisions, if any.</li>
                  </>
                )}
              </ul>
            </div>

            <div className="w8ben-form-group">
              <label className="w8ben-label" htmlFor="signatureName">
                Electronic Signature <span className="w8ben-required">*</span>
              </label>
              <input
                id="signatureName"
                type="text"
                className={`w8ben-input w8ben-signature-input ${errors.signatureName ? 'w8ben-input-error' : ''}`}
                value={formData.signatureName || ''}
                onChange={(e) => handleChange('signatureName', e.target.value)}
                placeholder="Type your full legal name"
              />
              {errors.signatureName && (
                <span className="w8ben-error-message">{errors.signatureName}</span>
              )}
            </div>

            <div className="w8ben-form-group">
              <label className="w8ben-label" htmlFor="signingCapacity">
                Capacity in Which Acting (if not beneficial owner)
              </label>
              <input
                id="signingCapacity"
                type="text"
                className="w8ben-input"
                value={formData.signingCapacity || ''}
                onChange={(e) => handleChange('signingCapacity', e.target.value)}
                placeholder="e.g., Guardian, Power of Attorney (optional)"
              />
              <p className="w8ben-field-hint">
                Leave blank if you are signing as the beneficial owner
              </p>
            </div>

            <div className="w8ben-form-group">
              <label className="w8ben-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.esignConsent || false}
                  onChange={(e) => handleChange('esignConsent', e.target.checked)}
                />
                <span>
                  I consent to using electronic signatures and records <span className="w8ben-required">*</span>
                </span>
              </label>
              {errors.esignConsent && (
                <span className="w8ben-error-message">{errors.esignConsent}</span>
              )}
            </div>

            <div className="w8ben-info-box">
              <h4>Electronic Signature Disclosure</h4>
              <p>
                By checking the box above and typing your name, you acknowledge and agree to the
                use of electronic signatures as provided in the Electronic Signatures in Global and
                National Commerce Act (ESIGN Act). Your electronic signature has the same legal
                effect as a handwritten signature.
              </p>
              <p className="w8ben-signature-date">
                <strong>Date:</strong> {new Date().toLocaleDateString('en-US')}
              </p>
            </div>

            <div className="w8ben-warning-box">
              <h4>⚠️ Important: Form Expiration</h4>
              <p>
                Form W-8 BEN will expire 3 years from the date of signing. You will need to submit
                a new form before it expires to continue receiving reduced withholding rates (if applicable).
              </p>
              <p>
                <strong>Expiration Date:</strong>{' '}
                {new Date(new Date().setFullYear(new Date().getFullYear() + 3)).toLocaleDateString('en-US')}
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w8ben-questionnaire-container">
      <div className="w8ben-form-header">
        <h2 className="w8ben-form-title">Form W-8 BEN</h2>
        <p className="w8ben-form-subtitle">
          Certificate of Foreign Status of Beneficial Owner for United States Tax Withholding and
          Reporting (Individuals)
        </p>
      </div>

      <div className="w8ben-form-content">{renderStep()}</div>

      <div className="w8ben-form-actions">
        {currentStep > 1 && (
          <button
            type="button"
            className="w8ben-button w8ben-button-secondary"
            onClick={handleBack}
            disabled={isSubmitting}
          >
            Back
          </button>
        )}
        <button
          type="button"
          className="w8ben-button w8ben-button-primary"
          onClick={handleNext}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : currentStep === totalSteps ? 'Submit' : 'Next'}
        </button>
      </div>

      {debug && (
        <div className="w8ben-debug-info">
          <h4>Debug Information</h4>
          <pre>{JSON.stringify({ currentStep, formData, errors }, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default W8BENQuestionnaire;
